import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from "react";
import moment from 'moment';
import { useEventContext } from "./EventContext";
import { useUserContext } from "./UserContext";
import { useStreamContext } from "./StreamContext";
import { TYPE_HYBRID } from "../constants/common";
import { episodeFavoriteToggle, getEpisodeFavoriteList } from "../utils/api";

export const ApplicationContext = createContext(null);

export const useApplicationContext = () => useContext(ApplicationContext);

export const MENU_TYPES = ["lobby", "broadcast", "halls", "program", "quiz", "members", "shop"];
export const HIDDEN_PAGES = ['profile'];

const TIME_DELAY = 3;

export const ApplicationContextProvider = ({ children }) => {
    const {pages, event, initData} = useEventContext();
    const {participationType, user} = useUserContext();
    const [currentEpisode, setCurrentEpisode] = useState(null);
    const {stream: currentStream} = useStreamContext();
    const [bookmarkEpisodeIds, setBookmarkEpisodeIds] = useState(initData?.favoriteEpisodesIds || []);
    const episodesRef = useRef();

    const clientId = user?.id || user?.clientRegistrationId;

    const filteredPages = useMemo(() => {
        return pages.filter((page) => {
            return page.path && page.visible && (page.participationType === 'hybrid' || page.participationType === participationType) && !HIDDEN_PAGES.includes(page.type);
        });
    }, [pages, participationType]);

    const mainMenu = useMemo(() => {
        return filteredPages
            .map((page) => MENU_TYPES.includes(page.type) ? { ...page } : null)
            .filter(page => page !== null).slice(0, 5);
    }, [filteredPages]);

    const episodes = useMemo(() => {
        if (!currentStream) return null;

        const episodes = currentStream.episode.filter((episode) =>
            !episode.hidden &&
            (!episode.participationType || episode.participationType === TYPE_HYBRID || episode.participationType === participationType)
        ).sort((a, b) => a.timeStart - b.timeStart);

        episodesRef.current = episodes;

        return episodes;
    }, [currentStream, participationType]);

    const getNextEpisodeStartTime = useCallback(() => {
        const currentTime = moment();
        const nextEpisode = Array.isArray(episodes) ? episodes.find(episode => currentTime.unix() < episode.timeStart) : null;

        return nextEpisode ? nextEpisode.timeStart : null;
    }, [episodes]);

    const getCurrentEpisode = useCallback(() => {
        const currentTime = moment().unix();
        return episodesRef.current?.find(episode => currentTime >= episode.timeStart && currentTime <= episode.timeStop);
    }, []);

    useEffect(() => {
        setCurrentEpisode(getCurrentEpisode());
    }, [episodes]);

    const getBookmarkEpisodeIds = async () => {
        try {
            const bookmarkEpisodeIds = await getEpisodeFavoriteList(event.eventKey);
            setBookmarkEpisodeIds(bookmarkEpisodeIds);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (clientId) {
            getBookmarkEpisodeIds();
        }
    }, [clientId]);

    useEffect(() => {
        const nextEpisodeStartTime = getNextEpisodeStartTime();

        if (nextEpisodeStartTime) {
            const timeToNextEpisode = nextEpisodeStartTime - moment().unix() + TIME_DELAY;
            const timeoutId = setTimeout(() => {
                setCurrentEpisode(getCurrentEpisode());
            }, timeToNextEpisode * 1000);

            return () => clearTimeout(timeoutId);
        }
    }, [getNextEpisodeStartTime, currentEpisode]);

    const handleBookmark = async (episoeId) => {
        const isBookmark = bookmarkEpisodeIds.includes(episoeId);
        if (!isBookmark) {
            setBookmarkEpisodeIds(ids => ids.concat([episoeId]));
        } else {
            setBookmarkEpisodeIds(ids => ids.filter((id) => id !== episoeId));
        }

        try {
            await episodeFavoriteToggle(episoeId, !isBookmark);
        } catch (err) {
            console.error(err);
        }
    }

    const context = {
        filteredPages,
        mainMenu,
        currentEpisode,
        bookmarkEpisodeIds,
        handleBookmark,
        setBookmarkEpisodeIds,
    };

    return (
        <ApplicationContext.Provider value={context}>
            {children}
        </ApplicationContext.Provider>
    );
}
